// src/utils/productPricing.js
// Unified Product Pricing Engine
// Handles all product pricing logic including variants and sales

import { getSalesByProductId } from '../firebase/sales-functions';

/**
 * Enrich cart items with current pricing and sales information
 * This replaces the complex enrichCartItemWithFreshData function
 * @param {array} cartItems - Minimal cart items from storage
 * @param {string} storeId - Store ID
 * @returns {array} Enriched cart items with current pricing
 */
export const enrichCartItemsWithPricing = async (cartItems, storeId) => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return [];
    }

    // Process all items in parallel for better performance
    const enrichedItems = await Promise.all(
        cartItems.map(item => enrichSingleCartItem(item, storeId))
    );

    // Filter out any items that couldn't be enriched (product not found)
    return enrichedItems.filter(item => !item.error);
};

/**
 * Enrich a single cart item with current pricing
 * @param {object} cartItem - Single cart item
 * @param {string} storeId - Store ID
 * @returns {object} Enriched cart item
 */
const enrichSingleCartItem = async (cartItem, storeId) => {
    try {
        // Import here to avoid circular dependency
        const { getProductById } = await import('../firebase/product-functions');

        // Get fresh product data
        const product = await getProductById(storeId, cartItem.productId);
        if (!product) {
            return { ...cartItem, error: 'Product not found' };
        }

        // Get the specific variant if item has one
        const variant = cartItem.variantId
            ? product.variants?.find(v => v.id === cartItem.variantId)
            : null;

        // Calculate pricing with sales
        const pricingData = await calculateItemPricing({
            product,
            variant,
            storeId
        });

        return {
            // Original cart item data
            ...cartItem,

            // Fresh product info (keep stored display data for performance)
            name: cartItem.name || product.name,
            image: cartItem.image || product.images?.[0] || null,

            // Current pricing
            price: pricingData.finalPrice,
            originalPrice: pricingData.originalPrice,

            // Discount information
            hasDiscount: pricingData.hasDiscount,
            discountInfo: pricingData.discountInfo,

            // Stock status
            inStock: variant ? variant.quantity > 0 : product.quantity > 0,
            availableQuantity: variant ? variant.quantity : product.quantity,

            // Metadata (for advanced features)
            productData: product,
            variantData: variant
        };
    } catch (error) {
        console.error(`Error enriching cart item ${cartItem.productId}:`, error);
        return { ...cartItem, error: error.message };
    }
};

/**
 * Calculate pricing for a product/variant combination
 * This handles all the sales logic in one place
 * @param {object} params - Pricing parameters
 * @param {object} params.product - Product data
 * @param {object} params.variant - Variant data (optional)
 * @param {string} params.storeId - Store ID
 * @returns {object} Pricing information
 */
export const calculateItemPricing = async ({ product, variant, storeId }) => {
    // Get base price
    const basePrice = variant?.price || product?.price || 0;

    try {
        // Get current sales for this product
        const salesData = await getSalesByProductId(storeId, product.id);

        // Find applicable sale using the same priority logic
        const applicableSale = findApplicableSale(salesData, variant?.id);

        if (applicableSale) {
            return {
                originalPrice: basePrice,
                finalPrice: parseFloat(applicableSale.discountedPrice),
                hasDiscount: true,
                discountInfo: {
                    hasDiscount: true,
                    discountLabel: applicableSale.discountType === "percentage"
                        ? `${applicableSale.discountValue}% OFF`
                        : `${applicableSale.discountValue} OFF`,
                    saleId: applicableSale.id,
                    isVariantDiscount: !!applicableSale.variantId,
                    saleDescription: applicableSale.description
                }
            };
        }

        return {
            originalPrice: basePrice,
            finalPrice: basePrice,
            hasDiscount: false,
            discountInfo: null
        };
    } catch (error) {
        console.error('Error calculating item pricing:', error);
        // Return base pricing if sales calculation fails
        return {
            originalPrice: basePrice,
            finalPrice: basePrice,
            hasDiscount: false,
            discountInfo: null
        };
    }
};

/**
 * Find applicable sale for a product/variant
 * Uses the same priority logic as before
 * @param {array} salesData - All sales for the product
 * @param {string} variantId - Variant ID (optional)
 * @returns {object|null} Applicable sale or null
 */
const findApplicableSale = (salesData, variantId = null) => {
    if (!salesData || salesData.length === 0) {
        return null;
    }

    // Priority 1: Variant-specific sales
    if (variantId) {
        const variantSale = salesData.find(sale => sale.variantId === variantId);
        if (variantSale) {
            return variantSale;
        }
    }

    // Priority 2: Product-level sales (applies to all variants)
    const productSale = salesData.find(sale => !sale.variantId);
    return productSale || null;
};

/**
 * Calculate display price data for UI components
 * Used in product detail pages and cart displays
 * @param {object} product - Product data
 * @param {object} variant - Selected variant
 * @param {array} salesData - Sales data for the product
 * @returns {object} Display price information
 */
export const calculateDisplayPricing = (product, variant, salesData) => {
    const basePrice = variant?.price || product?.price || 0;
    const applicableSale = findApplicableSale(salesData, variant?.id);

    if (applicableSale) {
        return {
            originalPrice: basePrice,
            discountedPrice: parseFloat(applicableSale.discountedPrice),
            hasDiscount: true,
            discountLabel: applicableSale.discountType === "percentage"
                ? `${applicableSale.discountValue}% OFF`
                : `${applicableSale.discountValue} OFF`,
            isVariantDiscount: !!applicableSale.variantId,
            saleDescription: applicableSale.description
        };
    }

    return {
        originalPrice: basePrice,
        discountedPrice: basePrice,
        hasDiscount: false,
        discountLabel: null,
        isVariantDiscount: false,
        saleDescription: null
    };
};

export default {
    enrichCartItemsWithPricing,
    calculateItemPricing,
    calculateDisplayPricing
};